// Resolve SDK config. The served atlas-navbar.js reads window.ATLAS_CONFIG (set by the host page
// or injected at deploy time); anything unset falls back to the deployed prod environment so the
// SDK works out of the box. These are all non-secret public identifiers.

import type { AtlasConfig, ConfigGlobals, RoutingMode } from "./types";

const DEFAULTS = {
  apiEndpoint: "https://20quplmu3g.execute-api.us-east-1.amazonaws.com",
  hostedUiDomain: "https://atlas-prod-075233098631.auth.us-east-1.amazoncognito.com",
  userPoolClientId: "3ugikqt7tvlf3ik994sbhn7d20",
  // Where Cognito redirects back after login. Must be a path that LOADS the SDK so the callback
  // (?code=...) can be exchanged — the Console root does. "/server-login" loads nothing (403), so
  // we land on the origin root and let the SDK's hasCallbackCode()/handleRedirectCallback() run.
  redirectPath: "/",
  // The custom-domain apex Atlas serves apps under (no leading dot). When the browser host is this
  // apex or one of its subdomains, Atlas runs in subdomain mode with cross-subdomain SSO; anything
  // else (the default *.cloudfront.net domain, localhost) stays path-based. Baked in like the other
  // prod identifiers above; override via window.ATLAS_CONFIG.appDomain for another environment.
  appDomain: "dev.tryorion.xyz",
};

interface RoutingInfo {
  routingMode: RoutingMode;
  sharedSession: boolean;
  isAuthOrigin: boolean;
  cookieDomain: string;
  authOrigin: string;
}

// Decide routing/auth shape from the browser host, so tenant apps need ZERO per-app config:
//   <app>.dev.tryorion.xyz -> subdomain mode (app from hostname), shared-cookie session, login bounces
//                             to the apex auth origin.
//   dev.tryorion.xyz (apex) -> path mode (serves the Console), but still shared-cookie + IS the auth
//                              origin (PKCE completes here; writes the shared session cookie).
//   *.cloudfront.net / localhost -> path mode, per-origin sessionStorage, login completes locally.
export function detectRouting(hostname: string, appDomain: string): RoutingInfo {
  const host = (hostname || "").split(":")[0];
  const isApex = host === appDomain;
  const isSubapp = host.endsWith("." + appDomain) && !isApex;
  const inFamily = isApex || isSubapp;
  return {
    routingMode: isSubapp ? "subdomain" : "path",
    sharedSession: inFamily, // use the parent-domain cookie (apex + all app subdomains share it)
    isAuthOrigin: isApex, // the apex is where Cognito callbacks complete
    cookieDomain: inFamily ? "." + appDomain : "",
    authOrigin: inFamily ? `https://${appDomain}/` : "",
  };
}

export function resolveConfig(globals: ConfigGlobals = globalThis as ConfigGlobals): AtlasConfig {
  const injected = (globals && globals.ATLAS_CONFIG) || {};
  const origin = globals?.location?.origin || "";
  const hostname = globals?.location?.hostname || "";
  const appDomain = injected.appDomain || DEFAULTS.appDomain;
  const r = detectRouting(hostname, appDomain);
  // In the custom-domain family, login always completes on the single apex auth origin (one Cognito
  // callback for unlimited subdomains). Elsewhere, the per-origin redirect (today's behavior).
  const redirectUri =
    injected.redirectUri || (r.authOrigin ? r.authOrigin : origin + (injected.redirectPath || DEFAULTS.redirectPath));
  return {
    apiEndpoint: stripTrailingSlash(injected.apiEndpoint || DEFAULTS.apiEndpoint),
    hostedUiDomain: stripTrailingSlash(injected.hostedUiDomain || DEFAULTS.hostedUiDomain),
    userPoolClientId: injected.userPoolClientId || DEFAULTS.userPoolClientId,
    redirectUri,
    routingMode: injected.routingMode || r.routingMode,
    sharedSession: r.sharedSession,
    isAuthOrigin: r.isAuthOrigin,
    cookieDomain: r.cookieDomain,
    authOrigin: r.authOrigin,
    appDomain,
  };
}

function stripTrailingSlash(s: string): string {
  return s.endsWith("/") ? s.slice(0, -1) : s;
}
